// @credit: 76a97b1cd4349534e19fb23cf80c4a0f1b1502418015de1e9d68b95c44bc36d433ed49b8
//
// Aphilaty PWA — Activate Admin Invite
//
// Public page. Reached via emailed link: /admin/activate?token={token}
// Creates the Firebase Auth account and org membership atomically on submit.

import { useState, useEffect, FormEvent } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { callFunction, AppError } from '../services/firebase';

interface InviteInfo {
  orgName: string;
  inviteeEmail: string;
  role: string;
}

const ROLE_LABELS: Record<string, string> = {
  admin:         'Organization Admin',
  org_creator:   'Organization Creator',
  org_approver:  'Content Approver',
};

export default function ActivateAdminInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [inviteInfo,      setInviteInfo]      = useState<InviteInfo | null>(null);
  const [validateError,   setValidateError]   = useState<string | null>(null);
  const [password,        setPassword]        = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isActivating,    setIsActivating]    = useState(false);
  const [activateError,   setActivateError]   = useState<string | null>(null);
  const [activated,       setActivated]       = useState(false);

  useEffect(() => {
    if (!token) {
      setValidateError('No invite token found. Check that you used the full link from your email.');
      return;
    }
    callFunction<InviteInfo>('validateAdminInviteToken', { token })
      .then(setInviteInfo)
      .catch((err: AppError) => {
        setValidateError(err.message ?? 'Invalid or expired invite link.');
      });
  }, [token]);

  const handleActivate = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setActivateError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setActivateError('Passwords do not match.');
      return;
    }
    setIsActivating(true);
    setActivateError(null);
    try {
      await callFunction('activateOrgAdminInvite', { token, password });
      setActivated(true);
    } catch (err) {
      setActivateError((err as AppError).message ?? 'Activation failed. Please try again.');
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--color-secondary)',
      padding: 24,
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 40, fontWeight: 800, color: 'var(--color-primary)',
            letterSpacing: -2, marginBottom: 6 }}>Ap</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 6 }}>
            Aphilaty Admin
          </div>
        </div>

        <div style={{ background: 'var(--color-bg)', borderRadius: 'var(--radius-lg)', padding: 28 }}>
          {/* Loading */}
          {!inviteInfo && !validateError && (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <span className="spinner" />
              <p style={{ marginTop: 12, color: 'var(--color-text-secondary)', fontSize: 13 }}>
                Checking invite…
              </p>
            </div>
          )}

          {/* Invalid token */}
          {validateError && (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ fontSize: 28, marginBottom: 12 }}>✕</div>
              <h1 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>Invite not valid</h1>
              <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.6,
                marginBottom: 16 }}>
                {validateError}
              </p>
              <p style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                If you think this is a mistake, contact your Aphilaty platform admin for a new invite.
              </p>
            </div>
          )}

          {/* Already activated */}
          {activated && (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
              <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Account activated</h1>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.6,
                marginBottom: 20 }}>
                Your account is ready. Sign in to start managing{' '}
                {inviteInfo?.orgName ?? 'your organization'}.
              </p>
              <Link to="/login" className="btn btn-primary"
                style={{ display: 'inline-block', textDecoration: 'none' }}>
                Sign In
              </Link>
            </div>
          )}

          {/* Activation form */}
          {inviteInfo && !activated && (
            <>
              <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
                Activate your account
              </h1>
              <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 2,
                lineHeight: 1.5 }}>
                You've been invited to administer <strong>{inviteInfo.orgName}</strong> as{' '}
                {ROLE_LABELS[inviteInfo.role] ?? inviteInfo.role}.
              </p>
              <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 20 }}>
                Account: {inviteInfo.inviteeEmail}
              </p>

              {activateError && (
                <div className="error-banner" role="alert" style={{ marginBottom: 16 }}>
                  {activateError}
                </div>
              )}

              <form onSubmit={handleActivate} noValidate>
                <div className="form-group">
                  <label className="form-label" htmlFor="password">Set a password</label>
                  <input id="password" type="password" className="form-input"
                    value={password} onChange={e => setPassword(e.target.value)}
                    autoComplete="new-password" required
                    placeholder="At least 8 characters" />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="confirm-password">Confirm password</label>
                  <input id="confirm-password" type="password" className="form-input"
                    value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    autoComplete="new-password" required
                    placeholder="Re-enter password" />
                </div>

                <button type="submit" className="btn btn-primary"
                  style={{ width: '100%', marginTop: 8 }} disabled={isActivating}>
                  {isActivating ? <span className="spinner" /> : 'Activate Account'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
